
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Box, Cpu, Zap, Search } from 'lucide-react';
import { GOOGLE_COLORS } from '../constants';

interface EquipmentItem {
  id: number;
  brand: string;
  type: string;
  serial: string;
}

const BRANDS = ['L-Acoustics', 'd&b audiotechnik', 'Robe', 'MA Lighting', 'Yamaha', 'Shure', 'Sennheiser', 'Barco', 'Christie', 'Pioneer DJ', 'Allen & Heath', 'Blackmagic Design'];
const TYPES = ['Line Array System', 'Moving Head Wash', 'Digital Mixing Console', 'Wireless IEM Set', '4K Laser Projector', 'LED Video Processor', 'Media Server', 'Power Distribution Unit'];

const EquipmentView: React.FC<{ onBack: () => void; onItemClick: (name: string) => void }> = ({ onBack, onItemClick }) => {
  const [items, setItems] = useState<EquipmentItem[]>([]);
  const [page, setPage] = useState(0);
  const loaderRef = useRef<HTMLDivElement>(null);

  const generateItems = (count: number, startId: number) => {
    return Array.from({ length: count }).map((_, i) => ({
      id: startId + i,
      brand: BRANDS[Math.floor(Math.random() * BRANDS.length)],
      type: TYPES[Math.floor(Math.random() * TYPES.length)],
      serial: `DX-${Math.random().toString(36).substring(2, 7).toUpperCase()}`
    }));
  };

  useEffect(() => {
    setItems(generateItems(12, 0));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (page > 0) {
      const newItems = generateItems(8, items.length);
      setItems((prev) => [...prev, ...newItems]);
    }
  }, [page]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center text-gray-600 hover:text-black transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="font-medium">Terug naar Deniz</span>
          </button>
          <h2 className="text-xl font-bold">
            <span style={{ color: GOOGLE_COLORS.blue }}>A</span>
            <span style={{ color: GOOGLE_COLORS.red }}>p</span>
            <span style={{ color: GOOGLE_COLORS.yellow }}>p</span>
            <span style={{ color: GOOGLE_COLORS.blue }}>a</span>
            <span style={{ color: GOOGLE_COLORS.green }}>r</span>
            <span style={{ color: GOOGLE_COLORS.red }}>a</span>
            <span style={{ color: GOOGLE_COLORS.yellow }}>t</span>
            <span style={{ color: GOOGLE_COLORS.blue }}>u</span>
            <span style={{ color: GOOGLE_COLORS.green }}>u</span>
            <span style={{ color: GOOGLE_COLORS.red }}>r</span>
          </h2>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 w-full">
        <p className="text-gray-500 mb-8 text-center italic">Klik op een item om de details en beschikbaarheid bij Deniz te bekijken.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((item) => (
            <div 
              key={item.id} 
              onClick={() => onItemClick(`${item.brand} ${item.type}`)}
              className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all group cursor-pointer active:scale-95"
            >
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <Cpu className="w-6 h-6 text-blue-600 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">{item.brand}</h3>
              <p className="text-gray-500 text-sm mb-4">{item.type}</p>
              <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                <span className="text-xs font-mono text-gray-400">{item.serial}</span>
                <div className="flex items-center text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Search className="w-4 h-4 mr-1" />
                  <span className="text-xs font-bold uppercase">Bekijk</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div ref={loaderRef} className="py-20 flex justify-center">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce [animation-delay:-.3s]"></div>
            <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce [animation-delay:-.5s]"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentView;
